import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Pencil, Trash2 } from 'lucide-react';
import { GarantieCommissionItemDto } from 'api/funnel';
import { Sizes } from 'enums/sizes';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { ExpressionVariable } from '../components/expression/ExpressionBuilder';
import { GuaranteeCommissionModal } from './GuaranteeCommissionModal';
import { PricingLine, PricingGroup } from '../components/PricingLines';
import { DASH, formatAmount } from '../funnelFormat';

interface GuaranteeCommissionsModalProps {
  garantieTunnelId: string;
  guaranteeLabel: string;
  variables: ExpressionVariable[];
  /**
   * Prévient la page qu'une commission a bougé.
   *
   * Une commission entre dans le prix du tunnel : sans ce rappel, la modale rafraîchit
   * bien sa propre colonne de tarification, mais le bloc Tarification de la page reste
   * sur le prix d'avant.
   */
  onChanged?: () => void;
}

/**
 * Commissions d'une garantie et tarification qui en résulte, côte à côte.
 *
 * Reprend la modale de l'ancien écran : la colonne de droite montre immédiatement
 * l'effet des commissions de gauche sur le prix. Elle est rechargée après chaque
 * mutation, ce que l'ancienne version ne faisait pas — il fallait rouvrir la modale
 * pour voir le nouveau prix.
 */
export const GuaranteeCommissionsModal: FC<GuaranteeCommissionsModalProps> = ({
  garantieTunnelId,
  guaranteeLabel,
  variables,
  onChanged,
}) => {
  const { openBlankDialog } = useDialog();
  const [toDelete, setToDelete] = useState<GarantieCommissionItemDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCommissions = useCallback(
    () => funnelsAPI.getGuaranteeCommissions({ garantieTunnelId }),
    [garantieTunnelId]
  );
  const commissions = useAsyncResource(fetchCommissions);

  const fetchPricing = useCallback(
    () => funnelsAPI.getFunnelGuaranteePricingDetails({ garantieTunnelId }),
    [garantieTunnelId]
  );
  const pricing = useAsyncResource(fetchPricing);

  const refresh = useCallback(async () => {
    await Promise.all([commissions.reload(), pricing.reload()]);
    onChanged?.();
  }, [commissions, pricing, onChanged]);

  const openCommission = (commissionId?: string) =>
    openBlankDialog(
      { size: Sizes['4xl'] },
      <GuaranteeCommissionModal
        garantieTunnelId={garantieTunnelId}
        commissionId={commissionId}
        guaranteeLabel={guaranteeLabel}
        variables={variables}
        onSuccess={refresh}
      />
    );

  const deleteCommission = async () => {
    if (!toDelete) return;

    setIsDeleting(true);
    try {
      const response = await funnelsAPI.deleteGuaranteeCommission({ id: toDelete.id });
      if (response.isSuccess) {
        toast.success('Commission supprimée');
        await refresh();
      } else {
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (error) {
      console.error('Suppression de la commission impossible', error);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  };

  const rows = commissions.data?.commissions ?? [];
  const tarif = pricing.data;

  return (
    <>
      <ModalShell title="Commissions et tarification" subtitle={`Garantie ${guaranteeLabel}`}>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Colonne gauche : les commissions */}
        <section className="rounded-2xl border border-gray-100 p-5">
          <h3 className="mb-4 text-lg font-semibold text-zinc-800">Commissions</h3>

          {commissions.isLoading ? (
            <GenericLoading />
          ) : commissions.error ? (
            <p className="py-6 text-center text-sm text-red-500">{commissions.error}</p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">
              Aucune commission sur cette garantie
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((commission) => (
                <li key={commission.id} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-semibold text-zinc-800">
                    {commission.typeCommission || DASH}
                    <span className="ml-1 font-normal text-gray-500">
                      | {commission.beneficiaire || DASH}
                    </span>
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      title="Modifier la commission"
                      aria-label="Modifier la commission"
                      onClick={() => openCommission(commission.id)}
                      className="flex h-8 w-9 items-center justify-center rounded-md bg-primary text-white transition-colors hover:bg-primary/90"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Supprimer la commission"
                      aria-label="Supprimer la commission"
                      onClick={() => setToDelete(commission)}
                      className="flex h-8 w-9 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button color="primary" className="mt-5" onClick={() => openCommission()}>
            Ajouter une commission
          </Button>
        </section>

        {/* Colonne droite : la tarification qui en résulte */}
        <section className="rounded-2xl border border-gray-100 p-5">
          <h3 className="mb-4 text-lg font-semibold text-zinc-800">Tarification</h3>

          {pricing.isLoading ? (
            <GenericLoading />
          ) : pricing.error ? (
            <p className="py-6 text-center text-sm text-red-500">{pricing.error}</p>
          ) : !tarif ? (
            <p className="py-6 text-center text-sm text-gray-400">
              Tarification indisponible pour cette garantie
            </p>
          ) : (
            <div className="flex flex-col">
              <PricingGroup>
                <PricingLine label="Prime pure" value={formatAmount(tarif.primePure)} />
              </PricingGroup>

              <PricingGroup>
                <PricingLine label="Somme des majorations" value={formatAmount(tarif.majoration)} />
                <PricingLine label="Somme des minorations" value={formatAmount(tarif.minoration)} />
                <PricingLine
                  label="Prime pure totale"
                  value={formatAmount(tarif.primePureTotal)}
                />
              </PricingGroup>

              {(tarif.remunerations ?? []).length > 0 && (
                <PricingGroup>
                  {(tarif.remunerations ?? []).map((remuneration, index) => (
                    <PricingLine
                      key={`${remuneration.remunerationId}-${index}`}
                      label={remuneration.libelle || DASH}
                      value={formatAmount(remuneration.montant)}
                    />
                  ))}
                </PricingGroup>
              )}

              <PricingGroup>
                <PricingLine label="Prix HT" value={formatAmount(tarif.prixHT)} />
                <PricingLine label="Taxe" value={formatAmount(tarif.taxe)} />
                <PricingLine label="Prix TTC" value={formatAmount(tarif.prixTTC)} />
              </PricingGroup>
            </div>
          )}
        </section>
        </div>
      </ModalShell>

      <ConfirmationOverlay
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={deleteCommission}
        isLoading={isDeleting}
        title="Supprimer cette commission ?"
        description={`La commission « ${toDelete?.typeCommission ?? ''} » sera retirée de la garantie.`}
        confirmLabel="Supprimer"
      />
    </>
  );
};
